import { useCallback } from "react";
import useMessage from "./useMessage";

/**
 * Returns two functions that writes text to the clipboard
 *
 * @example
 * ```tsx
 * const { copyText, copyLink } = useClipboard();
 *
 * copyText('Text to copy', 'Text copied successfully'); // -> Copies the text and renders the success feedback
 * copyLink('Link to copy'); // -> Copies the text and renders a success feedback with the message 'Link copiado'
 * ```
 */
export const useClipboard = () => {
  const message = useMessage();

  /**
   * Writes text to the clipboard and optionally renders a success feedback message
   */
  const copyText = useCallback(
    (
      /**
       * The text to be written to the clipboard
       */
      textToCopy?: string | null,
      /**
       * The feedback message to be rendered as a success snackbar
       */
      successMessage?: string,
    ) => {
      if (!textToCopy) return;

      const writeToClipboard = async () => {
        try {
          await navigator.clipboard.writeText(textToCopy);
          if (successMessage) void message.success(successMessage);
        } catch {
          // do nothing
        }
      };

      void writeToClipboard();
    },
    [message],
  );

  /**
   * Writes a link to the clipboard and always renders a success feedback message
   */
  const copyLink = useCallback(
    (
      /**
       * The link to be written to the clipboard
       */
      linkToCopy?: string | null,
      /**
       * The success feedback message to be rendered as a snackbar
       * @default "Link copiado"
       */
      successMessage = "Link copiado",
    ) => copyText(linkToCopy, successMessage),
    [copyText],
  );

  return { copyText, copyLink };
};
